import React from "react";
import { Box } from "@mui/material";
import { dt } from "../../../shared/themes/redesignTokens";
import { WorkOrderTypeCode } from "../models/models";
import useTranslation from "../../../shared/hooks/useTranslation";

interface TypeConfig {
  color: string;
  bg: string;
  translationKey: string;
}

const typeConfigs: Record<number, TypeConfig> = {
  [WorkOrderTypeCode.Reactive]: {
    color: "#E67E22",
    bg: "rgba(230, 126, 34, 0.08)",
    translationKey: "types.reactive",
  },
  [WorkOrderTypeCode.PreventiveMaintenance]: {
    color: "#3B82F6",
    bg: "rgba(59, 130, 246, 0.08)",
    translationKey: "types.preventiveMaintenance",
  },
  [WorkOrderTypeCode.PredictiveMaintenance]: {
    color: "#8E44AD",
    bg: "rgba(142, 68, 173, 0.08)",
    translationKey: "types.predictiveMaintenance",
  },
  [WorkOrderTypeCode.Inspection]: {
    color: "#16A085",
    bg: "rgba(22, 160, 133, 0.08)",
    translationKey: "types.inspection",
  },
  [WorkOrderTypeCode.GeneralTask]: {
    color: "#6B7280",
    bg: "rgba(107, 114, 128, 0.08)",
    translationKey: "types.generalTask",
  },
};

const lgBgMap: Record<number, string> = {
  [WorkOrderTypeCode.Reactive]: "#FEF3F0",
  [WorkOrderTypeCode.PreventiveMaintenance]: "#EBF5FB",
  [WorkOrderTypeCode.PredictiveMaintenance]: "#F5EEF8",
  [WorkOrderTypeCode.Inspection]: "#EAFAF1",
  [WorkOrderTypeCode.GeneralTask]: "#F4F6F9",
};

interface WorkOrderTypeBadgeProps {
  typeId: number;
  typeName?: string;
  size?: "sm" | "lg";
}

const WorkOrderTypeBadge = ({ typeId, typeName, size = "sm" }: WorkOrderTypeBadgeProps) => {
  const { translate } = useTranslation("operations");
  const config = typeConfigs[typeId] || typeConfigs[WorkOrderTypeCode.GeneralTask];
  const label = translate(config.translationKey) || typeName || "Task";
  const isLg = size === "lg";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: isLg ? "4px 12px 4px 10px" : "2px 6px",
        borderRadius: isLg ? "0 6px 6px 0" : "3px",
        backgroundColor: isLg ? (lgBgMap[typeId] || lgBgMap[WorkOrderTypeCode.GeneralTask]) : config.bg,
        fontSize: isLg ? "12px" : "10px",
        fontWeight: isLg ? 600 : 500,
        fontFamily: dt.font,
        color: config.color,
        whiteSpace: "nowrap",
        lineHeight: isLg ? "16px" : "12px",
        transition: "all 0.2s ease",
        ...(isLg ? { borderLeft: `3px solid ${config.color}` } : {}),
      }}
    >
      {label}
    </Box>
  );
};

export default WorkOrderTypeBadge;
